import React, { useState } from 'react';
import { Search, User, Mail, AlertCircle, X } from 'lucide-react';
import { User as UserType } from '../types/User';
import { approvalAPI } from '../services/ApprovalAPI';

interface UserSearchProps {
  onUserSelect: (user: UserType) => void;
  selectedUser: UserType | null;
  onReset: () => void;
}

const UserSearch: React.FC<UserSearchProps> = ({ onUserSelect, selectedUser, onReset }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualSapId, setManualSapId] = useState('');

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setError('Please enter a search term');
      return;
    }

    setIsLoading(true);
    setError('');
    setSearchResults([]);

    try {
      // Call the function to search for user by email
      const result = await approvalAPI.searchUserByEmail(searchTerm.trim());
      
      console.log('API Response:', result); // Debug log
      
      if (result.Success && result.Message === "notfound") {
        console.log('User not found, showing manual SAP ID form'); // Debug log
        
        // Handle "notfound" case - show user details if available and allow manual SAP ID entry
        const user: UserType = {
          id: result.UserDetails?.Id || 'temp-id',
          name: result.UserDetails?.DisplayName || 'Unknown User',
          email: result.UserEmail || searchTerm.trim(),
          sapId: result.UserDetails?.SapUsername || '', // Empty if no SAP ID found
          department: result.UserDetails?.Department || '',
          currentRoles: [], // Will be populated when user clicks "Get Current Roles"
          manager: result.ManagerDetails ? {
            id: result.ManagerDetails.Id,
            name: result.ManagerDetails.DisplayName,
            email: result.ManagerDetails.Email,
            sapId: result.ManagerDetails.SapUsername
          } : {
            id: 'default-manager',
            name: 'Manager Not Found',
            email: '',
            sapId: ''
          }
        };
        
        console.log('Created user object for notfound case:', user); // Debug log
        setSearchResults([user]);
      } else if (result.Success && result.UserDetails) {
        console.log('Parsing user data:', result.UserDetails); // Debug log
        
        // Convert the API response to UserType format
        const user: UserType = {
          id: result.UserDetails.Id,
          name: result.UserDetails.DisplayName,
          email: result.UserDetails.Email,
          sapId: result.UserDetails.SapUsername,
          department: result.UserDetails.Department,
          currentRoles: [], // We'll populate this when we get role data
          manager: result.ManagerDetails ? {
            id: result.ManagerDetails.Id,
            name: result.ManagerDetails.DisplayName,
            email: result.ManagerDetails.Email,
            sapId: result.ManagerDetails.SapUsername
          } : {
            id: 'default-manager',
            name: 'Manager Not Found',
            email: '',
            sapId: ''
          }
        };
        
        console.log('Created user object:', user); // Debug log
        setSearchResults([user]);
      } else {
        console.log('API call failed or no user details:', result); // Debug log
        setError(result.Message || 'User not found');
      }
    } catch (error) {
      console.error('Search error:', error);
      setError('Failed to search for user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserClick = async (user: UserType) => {
    // Don't automatically fetch current roles - let user click "Get Current Roles" button
    onUserSelect(user);
    setSearchResults([]);
    setSearchTerm('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="card">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Search User</h3>
            <p className="text-gray-600">Enter an email address to find a user</p>
          </div>
          {selectedUser && (
            <button
              onClick={onReset}
              className="btn-secondary flex items-center"
            >
              <X size={16} className="mr-2" />
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Selected User Display */}
      {selectedUser && (
        <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900">{selectedUser.name}</h4>
              <p className="text-sm text-gray-600">{selectedUser.email}</p>
              <p className="text-sm text-gray-500">SAP ID: {selectedUser.sapId} • {selectedUser.department}</p>
              <p className="text-sm text-gray-500">Manager: {selectedUser.manager.name}</p>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-primary-700">
                {selectedUser.currentRoles.length} role{selectedUser.currentRoles.length !== 1 ? 's' : ''}
              </div>
              <div className="text-xs text-gray-600">
                {selectedUser.currentRoles.slice(0, 2).join(', ')}
                {selectedUser.currentRoles.length > 2 && ` +${selectedUser.currentRoles.length - 2} more`}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Only show search interface if no user is selected */}
      {!selectedUser && (
        <div className="space-y-4">
          {/* Search Input */}
          <div className="flex space-x-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter email address..."
                className="input-field pl-10"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="btn-primary flex items-center disabled:opacity-50"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <Search size={16} className="mr-2" />
                  Search
                </>
              )}
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center text-red-600 bg-red-50 p-3 rounded-lg">
              <AlertCircle size={16} className="mr-2" />
              {error}
            </div>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Search Results</h4>
              {searchResults.map((user) => (
                <div
                  key={user.id}
                  className="p-4 border border-gray-200 rounded-lg transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h5 className="font-medium text-gray-900">{user.name}</h5>
                      <p className="text-sm text-gray-600">{user.email}</p>
                      <p className="text-sm text-gray-500">SAP ID: {user.sapId || 'Not found'} • {user.department}</p>
                      <p className="text-sm text-gray-500">Manager: {user.manager.name}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">
                        {user.currentRoles.length} role{user.currentRoles.length !== 1 ? 's' : ''}
                      </div>
                      <div className="text-xs text-gray-500">
                        {user.currentRoles.slice(0, 2).join(', ')}
                        {user.currentRoles.length > 2 && ` +${user.currentRoles.length - 2} more`}
                      </div>
                    </div>
                  </div>
                  
                  {/* Manual SAP ID input for users without SAP ID */}
                  {!user.sapId && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800 mb-2">
                        <strong>SAP ID not found.</strong> Please enter your SAP ID manually:
                      </p>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={manualSapId}
                          onChange={(e) => setManualSapId(e.target.value)}
                          placeholder="Enter your SAP ID"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <button
                          onClick={() => {
                            if (manualSapId.trim()) {
                              const userWithSapId = { ...user, sapId: manualSapId.trim() };
                              handleUserClick(userWithSapId);
                            }
                          }}
                          disabled={!manualSapId.trim()}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Continue
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Select button for users with SAP ID */}
                  {user.sapId && (
                    <button
                      onClick={() => handleUserClick(user)}
                      className="w-full mt-3 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Select User
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSearch;
